import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { employee } from '../../model/master';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppDispatch } from '../../store';
import { increase } from '../../store/user/slice';
import { isProductionApi } from '../../api/api-config';
import '../../assets/css/employee-permissions.css';

interface Props {
    employee: employee;
    onChange: React.Dispatch<React.SetStateAction<object>>;
    onSave?: () => void;
    onCancel?: () => void;
    showStandaloneButtons?: boolean;
}

export interface PermissionCategory {
    categoryKey: string;
    categoryTitle: string;
    icon: string;
    items: {
        id: string;
        name: string;
        path?: string;
        defaultCashier: boolean;
        description?: string;
    }[];
}

// โครงสร้างสิทธิ์ที่สอดคล้องกับหน้าจอและฟังก์ชันจริงในระบบ JinDa POS
export const SYSTEM_PERMISSION_GROUPS: PermissionCategory[] = [
    {
        categoryKey: 'pos_actions',
        categoryTitle: '1. การขายหน้าร้านและการทำงาน (POS & Cashier Actions)',
        icon: 'dollar-sign',
        items: [
            { id: 'sale', name: 'ขายสินค้าหน้าร้าน (/sale)', path: '/sale', defaultCashier: true },
            { id: 'apply_discount', name: 'ทำส่วนลด / ส่วนลดท้ายบิล', defaultCashier: true },
            { id: 'hold_bill', name: 'พักบิล / เรียกลิสต์บิลที่พัก', defaultCashier: true },
            { id: 'cancel_sale', name: 'ยกเลิกรายการขาย', defaultCashier: false },
            { id: 'edit_items', name: 'แก้ไขจำนวน/ราคารายการ', defaultCashier: true },
            { id: 'delete_items', name: 'ลบรายการสินค้าออกจากบิล', defaultCashier: false },
            { id: 'open_drawer', name: 'เปิดลิ้นชักเก็บเงิน', defaultCashier: true },
            { id: 'work_shift', name: 'เปิด - ปิดกะการทำงาน', defaultCashier: true },
            { id: 'shortcut', name: 'ใช้งานเมนูลัดสินค้า', defaultCashier: true },
            { id: 'print_receipt', name: 'พิมพ์ใบเสร็จรับเงิน', defaultCashier: true }
        ]
    },
    {
        categoryKey: 'inventory',
        categoryTitle: '2. จัดการสินค้าและสต๊อก (Products & Inventory)',
        icon: 'boxes-stacked',
        items: [
            { id: 'product', name: 'ข้อมูลสินค้า (/product)', path: '/product', defaultCashier: false },
            { id: 'purchase_order', name: 'สั่งซื้อสินค้า PO (/purchase/order)', path: '/purchase/order', defaultCashier: false },
            { id: 'good_receive', name: 'รับเข้าสินค้า (/good/receive)', path: '/good/receive', defaultCashier: false },
            { id: 'consignment', name: 'สินค้าฝากขาย (/consignment)', path: '/consignment', defaultCashier: false },
            { id: 'check_stock', name: 'ตรวจนับสต๊อก (/check/stock)', path: '/check/stock', defaultCashier: true },
            { id: 'separate', name: 'แยกแพคสินค้า (/separate)', path: '/separate', defaultCashier: false },
            { id: 'include', name: 'รวมแพคสินค้า (/include)', path: '/include', defaultCashier: false },
            { id: 'withdraw', name: 'เบิกสินค้าบริการ (/withdraw)', path: '/withdraw', defaultCashier: false }
        ]
    },
    {
        categoryKey: 'member',
        categoryTitle: '3. จัดการสมาชิก (Member Management)',
        icon: 'users',
        items: [
            { id: 'member', name: 'ข้อมูลสมาชิก (/member)', path: '/member', defaultCashier: true },
            { id: 'member_group', name: 'กลุ่มสมาชิก (/member/group)', path: '/member/group', defaultCashier: false }
        ]
    },
    {
        categoryKey: 'master_data',
        categoryTitle: '4. ข้อมูลหลักและผู้ขาย (Master Data & Vendors)',
        icon: 'database',
        items: [
            { id: 'vendor', name: 'ผู้ขายส่งสินค้า (/vendor)', path: '/vendor', defaultCashier: false },
            { id: 'employee', name: 'พนักงานและสิทธิ์ (/employee)', path: '/employee', defaultCashier: false },
            { id: 'product_type', name: 'ประเภทสินค้า (/product/type)', path: '/product/type', defaultCashier: false },
            { id: 'product_unit', name: 'หน่วยสินค้า (/product/unit)', path: '/product/unit', defaultCashier: false },
            { id: 'payment_type', name: 'ประเภทการชำระเงิน', path: '/payment/type', defaultCashier: false }
        ]
    },
    {
        categoryKey: 'settings_reports',
        categoryTitle: '5. การตั้งค่าระบบและรายงาน (Settings & Reports)',
        icon: 'sliders',
        items: [
            { id: 'expense', name: 'บันทึกค่าใช้จ่าย (/expense)', path: '/expense', defaultCashier: false },
            { id: 'config_general', name: 'ตั้งค่าทั่วไป (/config/general)', path: '/config/general', defaultCashier: false },
            { id: 'config_separate', name: 'ตั้งค่าการแยกแพค (/config/separate)', path: '/config/separate', defaultCashier: false },
            { id: 'report', name: 'รายงานสรุปของระบบ', path: '/report', defaultCashier: false }
        ]
    }
];

// รวบรวม ID สิทธิ์ทั้งหมด
export const ALL_PERMISSION_IDS: string[] = SYSTEM_PERMISSION_GROUPS.flatMap(g => g.items.map(i => i.id));

// สิทธิ์เริ่มต้นของแคชเชียร์
export const CASHIER_DEFAULT_PERMISSION_IDS: string[] = SYSTEM_PERMISSION_GROUPS.flatMap(g =>
    g.items.filter(i => i.defaultCashier).map(i => i.id)
);

const EmployeeDetail: React.FC<Props> = ({
    employee: initialData,
    onChange,
    onSave,
    onCancel,
    showStandaloneButtons = false
}) => {
    const dispatch = useAppDispatch();

    // Form fields state.
    //
    // Every box below was pre-filled with a demo identity -- username
    // "admin", the name "ผู้จัดการ ร้านค้า" split across two fields, a
    // phone number, an email and the literal word "ที่อยู่". Blanking
    // `initEmployee` in the model was not enough: this component sets its
    // own defaults, so the form went on offering them. A fix has to reach
    // every layer that can supply the value.
    const [username, setUsername] = useState('');
    // Never pre-filled. On an existing employee an empty box means "leave
    // the current password alone"; the stored hash is deliberately not
    // loaded here (see the effect below).
    const [password, setPassword] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [useShift, setUseShift] = useState(false);
    const [prefix, setPrefix] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [address, setAddress] = useState('');

    // Role state
    const [role, setRole] = useState<'manager' | 'cashier' | 'custom'>('cashier');

    // Selected Permissions
    const [selectedPerms, setSelectedPerms] = useState<string[]>(() => CASHIER_DEFAULT_PERMISSION_IDS);

    const handleTestThisUser = () => {
        const testUser = {
            id: initialData?.id || 99,
            name: `${firstName} ${lastName}`.trim() || username || 'พนักงานทดสอบ',
            code: username || 'EMP_TEST',
            privilege: role === 'manager' ? 1 : 2,
            privilege_name: role === 'manager' ? 'ผู้จัดการ' : role === 'cashier' ? 'แคชเชียร์' : 'กำหนดเอง',
            role: role,
            permissions: selectedPerms,
            default_page: selectedPerms.includes('sale') ? 'sale' : (selectedPerms[0] || 'sale')
        };
        // Phase 7C-4: in-memory preview only. Nothing is written to
        // localStorage; a refresh restores the real actor from SESSION_ME.
        dispatch(increase(testUser));
        Modal.success({
            title: 'สลับสิทธิ์ทดสอบสำเร็จ! 🎉',
            content: `ระบบสลับเข้าใช้งานด้วยสิทธิ์ของ "${testUser.name}" (${selectedPerms.length} สิทธิ์) เรียบร้อยแล้ว แถบเมนูด้านบนจะแสดงเฉพาะหน้าที่ได้รับสิทธิ์ทันที`,
            okText: 'ตกลง',
            centered: true
        });
    };

    // An existing employee already has a password; a new one must be given
    // one. This drives whether the password box is optional.
    const isExistingEmployee = Number(initialData?.id || 0) > 0;

    // Which record the password box was last cleared for. The sync effect
    // below re-runs on EVERY render -- employee.tsx passes a fresh
    // `{...employee}` object, so `initialData` is a new identity each time
    // -- and the parent round-trips each keystroke back through it. The
    // other fields survive that because the value coming back is the one
    // just typed; the password box would not if it were cleared
    // unconditionally, so it is cleared once per record instead.
    const passwordClearedForId = useRef<number | null>(null);

    // Sync from props on mount or change
    useEffect(() => {
        if (initialData) {
            if (initialData.username !== undefined) setUsername(initialData.username);
            else if (initialData.code) setUsername(initialData.code);

            // The stored password hash is NOT loaded into the field. Doing
            // so put a credential into an editable input and, on save, sent
            // it back as if it were a new plaintext password -- which
            // replaced the account's password with its own hash and locked
            // the owner out.
            //
            // Cleared once per record (so a password typed for one employee
            // can never carry over to the next one opened), then left alone
            // so the operator can actually type into it -- see the ref above.
            const syncingEmployeeId = Number(initialData.id || 0);
            if (passwordClearedForId.current !== syncingEmployeeId) {
                passwordClearedForId.current = syncingEmployeeId;
                setPassword('');
            } else if (initialData.password !== undefined) {
                setPassword(initialData.password);
            }
            if (initialData.is_active !== undefined) setIsActive(Boolean(initialData.is_active));
            if (initialData.use_shift !== undefined) setUseShift(Boolean(initialData.use_shift));
            if (initialData.prefix !== undefined) setPrefix(initialData.prefix);
            if (initialData.first_name !== undefined) setFirstName(initialData.first_name);
            if (initialData.last_name !== undefined) setLastName(initialData.last_name);
            if (initialData.tel !== undefined) setPhone(initialData.tel);
            if (initialData.email !== undefined) setEmail(initialData.email);
            if (initialData.address !== undefined) setAddress(initialData.address);

            if (initialData.role && ['manager', 'cashier', 'custom'].includes(initialData.role)) {
                setRole(initialData.role as 'manager' | 'cashier' | 'custom');
            }

            if (initialData.permissions && Array.isArray(initialData.permissions)) {
                setSelectedPerms(initialData.permissions);
            } else if (initialData.menu_permissions && Array.isArray(initialData.menu_permissions)) {
                const combined = [
                    ...initialData.menu_permissions,
                    ...(initialData.action_permissions || [])
                ];
                setSelectedPerms(Array.from(new Set(combined)));
            }
        }
    }, [initialData]);

    // Push changes back to parent
    const emitChange = (updatedState: Partial<any>) => {
        const currentPerms = updatedState.selectedPerms ?? selectedPerms;
        const currentRole = updatedState.role ?? role;

        const fullData = {
            ...initialData,
            username: updatedState.username ?? username,
            code: updatedState.username ?? username,
            password: updatedState.password ?? password,
            name: `${updatedState.firstName ?? firstName} ${updatedState.lastName ?? lastName}`.trim(),
            first_name: updatedState.firstName ?? firstName,
            last_name: updatedState.lastName ?? lastName,
            prefix: updatedState.prefix ?? prefix,
            tel: updatedState.phone ?? phone,
            email: updatedState.email ?? email,
            address: updatedState.address ?? address,
            is_active: (updatedState.isActive ?? isActive) ? 1 : 0,
            use_shift: (updatedState.useShift ?? useShift) ? 1 : 0,
            role: currentRole,
            permissions: currentPerms,
            menu_permissions: currentPerms,
            action_permissions: currentPerms,
            // Only true when a new password was actually entered, so an
            // untouched box never rewrites the stored credential.
            isPasswordChange: (updatedState.password ?? password) !== ''
        };
        onChange(fullData);
    };

    // Toggle single permission
    const togglePerm = (id: string) => {
        const updated = selectedPerms.includes(id)
            ? selectedPerms.filter(item => item !== id)
            : [...selectedPerms, id];
        setSelectedPerms(updated);
        emitChange({ selectedPerms: updated, role: 'custom' });
        setRole('custom');
    };

    // Toggle Select All
    const isAllSelected = selectedPerms.length === ALL_PERMISSION_IDS.length;

    const handleSelectAll = (checked: boolean) => {
        const newPerms = checked ? [...ALL_PERMISSION_IDS] : [];
        setSelectedPerms(newPerms);
        emitChange({
            selectedPerms: newPerms,
            role: checked ? 'manager' : 'custom'
        });
        setRole(checked ? 'manager' : 'custom');
    };

    // Handle Role Change
    const handleRoleChange = (newRole: 'manager' | 'cashier' | 'custom') => {
        setRole(newRole);
        let newPerms: string[] = [];

        if (newRole === 'manager') {
            newPerms = [...ALL_PERMISSION_IDS];
        } else if (newRole === 'cashier') {
            newPerms = [...CASHIER_DEFAULT_PERMISSION_IDS];
        } else {
            newPerms = [...selectedPerms];
        }

        setSelectedPerms(newPerms);
        emitChange({
            role: newRole,
            selectedPerms: newPerms
        });
    };

    return (
        <div className="employee-edit-wrapper" data-testid="employee-edit-wrapper">
            <div className="employee-edit-title">
                <FontAwesomeIcon icon="user-shield" />
                <span>แก้ไขผู้ใช้งานและการกำหนดสิทธิ์</span>
            </div>

            {/* ✨ TOP SECTION: Polished Modern UX/UI */}
            <div className="employee-top-section">
                
                {/* 1. Left: Avatar Card */}
                <div className="employee-avatar-card">
                    <div className="avatar-preview-circle" title="คลิกเพื่อเลือกรูปภาพโปรไฟล์">
                        <FontAwesomeIcon icon="user" className="avatar-main-icon" />
                        <div className="avatar-camera-badge">
                            <FontAwesomeIcon icon="camera" />
                        </div>
                    </div>
                    <button type="button" className="avatar-upload-action" onClick={() => alert("เลือกไฟล์รูปภาพ (PNG/JPG)")}>
                        <FontAwesomeIcon icon="image" />
                        <span>เปลี่ยนรูปโปรไฟล์</span>
                    </button>
                    <div className="avatar-specs-hint">740 × 660 px (สูงสุด 5MB)</div>
                </div>

                {/* 2. Center: Clean 2-Column Form */}
                <div className="employee-form-container">
                    <div className="employee-form-grid">
                        {/* Row 1: Username & Password */}
                        <div className="form-field-group">
                            <label className="form-field-label required">รหัสผู้ใช้งาน</label>
                            <div className="input-with-icon">
                                <FontAwesomeIcon icon="user" className="input-icon-left" />
                                <input
                                    type="text"
                                    className="employee-form-input has-icon"
                                    value={username}
                                    placeholder="เช่น admin, EMP01"
                                    data-testid="input-username"
                                    onChange={e => {
                                        setUsername(e.target.value);
                                        emitChange({ username: e.target.value });
                                    }}
                                />
                            </div>
                        </div>

                        <div className="form-field-group">
                            <label className={`form-field-label${isExistingEmployee ? '' : ' required'}`}>รหัสผ่าน</label>
                            <div className="input-with-icon">
                                <FontAwesomeIcon icon="lock" className="input-icon-left" />
                                <input
                                    type="password"
                                    className="employee-form-input has-icon"
                                    value={password}
                                    placeholder={isExistingEmployee
                                        ? 'เว้นว่างไว้ถ้าไม่ต้องการเปลี่ยนรหัสผ่าน'
                                        : 'ป้อนรหัสผ่าน'}
                                    autoComplete="new-password"
                                    data-testid="input-password"
                                    onChange={e => {
                                        setPassword(e.target.value);
                                        emitChange({ password: e.target.value });
                                    }}
                                />
                            </div>
                        </div>

                        {/* Row 2: Prefix + First Name & Last Name */}
                        <div className="form-field-group">
                            <label className="form-field-label required">คำนำหน้า & ชื่อ</label>
                            <div className="prefix-name-inline-group">
                                <select
                                    className="employee-form-select employee-prefix-select"
                                    value={prefix}
                                    data-testid="select-prefix"
                                    onChange={e => {
                                        setPrefix(e.target.value);
                                        emitChange({ prefix: e.target.value });
                                    }}
                                >
                                    <option value="">เลือก</option>
                                    <option value="นาย">นาย</option>
                                    <option value="นาง">นาง</option>
                                    <option value="นางสาว">น.ส.</option>
                                </select>
                                <button
                                    type="button"
                                    className="btn-add-prefix-action"
                                    title="เพิ่มคำนำหน้าใหม่"
                                    onClick={() => alert("เพิ่มคำนำหน้า")}
                                >
                                    +
                                </button>
                                <input
                                    type="text"
                                    className="employee-form-input"
                                    value={firstName}
                                    placeholder="ชื่อจริง"
                                    data-testid="input-firstname"
                                    onChange={e => {
                                        setFirstName(e.target.value);
                                        emitChange({ firstName: e.target.value });
                                    }}
                                />
                            </div>
                        </div>

                        <div className="form-field-group">
                            <label className="form-field-label required">นามสกุล</label>
                            <input
                                type="text"
                                className="employee-form-input"
                                value={lastName}
                                placeholder="นามสกุล"
                                data-testid="input-lastname"
                                onChange={e => {
                                    setLastName(e.target.value);
                                    emitChange({ lastName: e.target.value });
                                }}
                            />
                        </div>

                        {/* Row 3: Phone & Email */}
                        <div className="form-field-group">
                            <label className="form-field-label">เบอร์โทรศัพท์</label>
                            <div className="input-with-icon">
                                <FontAwesomeIcon icon="phone" className="input-icon-left" />
                                <input
                                    type="text"
                                    className="employee-form-input has-icon"
                                    value={phone}
                                    placeholder="08x-xxx-xxxx"
                                    data-testid="input-phone"
                                    onChange={e => {
                                        setPhone(e.target.value);
                                        emitChange({ phone: e.target.value });
                                    }}
                                />
                            </div>
                        </div>

                        <div className="form-field-group">
                            <label className="form-field-label">อีเมล</label>
                            <div className="input-with-icon">
                                <FontAwesomeIcon icon="envelope" className="input-icon-left" />
                                <input
                                    type="email"
                                    className="employee-form-input has-icon"
                                    value={email}
                                    placeholder="name@example.com"
                                    data-testid="input-email"
                                    onChange={e => {
                                        setEmail(e.target.value);
                                        emitChange({ email: e.target.value });
                                    }}
                                />
                            </div>
                        </div>

                        {/* Row 4: Address (Full Width) */}
                        <div className="form-field-group full-width">
                            <label className="form-field-label">ที่อยู่</label>
                            <div className="input-with-icon">
                                <FontAwesomeIcon icon="location-dot" className="input-icon-left" />
                                <input
                                    type="text"
                                    className="employee-form-input has-icon"
                                    value={address}
                                    placeholder="ที่อยู่ / รายละเอียดที่ติดต่อได้"
                                    data-testid="input-address"
                                    onChange={e => {
                                        setAddress(e.target.value);
                                        emitChange({ address: e.target.value });
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Status Toggles Bar */}
                    <div className="status-toggles-bar">
                        <label className={`status-pill-toggle ${isActive ? 'active' : ''}`}>
                            <span className="toggle-indicator"></span>
                            <input
                                type="checkbox"
                                checked={isActive}
                                data-testid="checkbox-is-active"
                                onChange={e => {
                                    setIsActive(e.target.checked);
                                    emitChange({ isActive: e.target.checked });
                                }}
                            />
                            <span>เปิดใช้งานบัญชี (Active)</span>
                        </label>

                        <label className={`status-pill-toggle ${useShift ? 'active' : ''}`}>
                            <span className="toggle-indicator"></span>
                            <input
                                type="checkbox"
                                checked={useShift}
                                data-testid="checkbox-use-shift"
                                onChange={e => {
                                    setUseShift(e.target.checked);
                                    emitChange({ useShift: e.target.checked });
                                }}
                            />
                            <span>ใช้งานระบบกะทำงาน (Shift)</span>
                        </label>
                    </div>
                </div>

                {/* 3. Right: Interactive Role Selector Cards */}
                <div className="employee-role-card">
                    <div className="role-card-header">
                        <FontAwesomeIcon icon="crown" />
                        <span>บทบาทผู้ใช้งาน</span>
                    </div>

                    <div className="role-options-stack">
                        {/* Option 1: Manager */}
                        <div
                            className={`role-tile-item ${role === 'manager' ? 'selected' : ''}`}
                            onClick={() => handleRoleChange('manager')}
                        >
                            <div className="role-tile-radio"></div>
                            <FontAwesomeIcon icon="crown" className="role-tile-icon" />
                            <div className="role-tile-info">
                                <div className="role-tile-title">ผู้จัดการร้านค้า</div>
                                <div className="role-tile-desc">สิทธิ์เข้าถึงทุกฟังก์ชันในระบบ</div>
                            </div>
                            <input
                                type="radio"
                                name="role"
                                value="manager"
                                checked={role === 'manager'}
                                data-testid="radio-manager"
                                onChange={() => handleRoleChange('manager')}
                            />
                        </div>

                        {/* Option 2: Cashier */}
                        <div
                            className={`role-tile-item ${role === 'cashier' ? 'selected' : ''}`}
                            onClick={() => handleRoleChange('cashier')}
                        >
                            <div className="role-tile-radio"></div>
                            <FontAwesomeIcon icon="store" className="role-tile-icon" />
                            <div className="role-tile-info">
                                <div className="role-tile-title">พนักงานขายหน้าร้าน</div>
                                <div className="role-tile-desc">งานขาย แคชเชียร์ & นับสต๊อก</div>
                            </div>
                            <input
                                type="radio"
                                name="role"
                                value="cashier"
                                checked={role === 'cashier'}
                                data-testid="radio-cashier"
                                onChange={() => handleRoleChange('cashier')}
                            />
                        </div>

                        {/* Option 3: Custom */}
                        <div
                            className={`role-tile-item ${role === 'custom' ? 'selected' : ''}`}
                            onClick={() => handleRoleChange('custom')}
                        >
                            <div className="role-tile-radio"></div>
                            <FontAwesomeIcon icon="gear" className="role-tile-icon" />
                            <div className="role-tile-info">
                                <div className="role-tile-title">กำหนดเอง (Custom)</div>
                                <div className="role-tile-desc">เลือกสิทธิ์การใช้งานได้อย่างอิสระ</div>
                            </div>
                            <input
                                type="radio"
                                name="role"
                                value="custom"
                                checked={role === 'custom'}
                                data-testid="radio-custom"
                                onChange={() => handleRoleChange('custom')}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Permissions by Category - Matched to actual codebase pages & actions */}
            <div className="permissions-categories-container" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {SYSTEM_PERMISSION_GROUPS.map(group => (
                    <div key={group.categoryKey} className="permission-group-card">
                        <div className="permission-group-title">
                            <span>{group.categoryTitle}</span>
                        </div>
                        <div className="permission-items-grid">
                            {group.items.map(item => {
                                const isActive = selectedPerms.includes(item.id);
                                return (
                                    <div
                                        key={item.id}
                                        className={`perm-badge-item ${isActive ? 'active' : ''}`}
                                        data-testid={`badge-${item.id}`}
                                        onClick={() => togglePerm(item.id)}
                                        title={item.name}
                                    >
                                        <span className="badge-checkbox-box"></span>
                                        <span>{item.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Bottom Controls Section */}
            <div className="employee-bottom-bar">
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                    <label className="employee-checkbox-label">
                        <input
                            type="checkbox"
                            checked={isAllSelected}
                            data-testid="checkbox-select-all"
                            onChange={e => handleSelectAll(e.target.checked)}
                        />
                        เลือกทั้งหมด ({selectedPerms.length}/{ALL_PERMISSION_IDS.length} รายการ)
                    </label>

                    {/* Same rule as the role preview in the top-right menu:
                        this impersonates an employee in memory so the menu
                        can be demonstrated, and it has no business on the
                        live shop. Hidden whenever this build points at the
                        Production Backend; kept on localhost, the LAN and
                        the Pilot, which are where it is useful. */}
                    {!isProductionApi() && (
                        <button
                            type="button"
                            className="btn-test-perm"
                            onClick={handleTestThisUser}
                            title="ทดลองสลับระบบเป็นสิทธิ์ของพนักงานคนนี้ทันที"
                        >
                            <FontAwesomeIcon icon="bolt" />
                            <span>⚡ ทดสอบสิทธิ์ของคนนี้ทันที</span>
                        </button>
                    )}
                </div>

                {/* Only render action buttons if standalone */}
                {showStandaloneButtons && (
                    <div className="employee-standalone-actions">
                        <button
                            type="button"
                            className="btn-save"
                            data-testid="btn-save"
                            onClick={onSave}
                        >
                            <FontAwesomeIcon icon="floppy-disk" /> บันทึก
                        </button>
                        <button
                            type="button"
                            className="btn-cancel"
                            data-testid="btn-cancel"
                            onClick={onCancel}
                        >
                            <FontAwesomeIcon icon="xmark" /> ยกเลิก
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmployeeDetail;